import styled, { css, keyframes } from 'styled-components';

export const TopbarContainer = styled.div<{ $hidden?: boolean }>`
  ${({ theme, $hidden }) => css`
    display: flex;
    align-items: center;
    gap: 16px;
    height: 56px;
    min-width: 473px;
    box-sizing: border-box;
    padding: 2px 16px 2px 2px;
    border-radius: 1000px;
    border: none;
    background: linear-gradient(
      180deg,
      ${theme.v3.colors.brand.snappyBlue[50]} 0%,
      ${theme.v3.colors.brand.snappyBlue[70]} 100%
    );
    backdrop-filter: blur(16px);
    visibility: ${$hidden ? 'hidden' : 'visible'};

    @media (max-width: ${theme.v3.breakpoints.md}) {
      flex-wrap: wrap;
      width: 100%;
      min-width: 0;
      height: auto;
      box-sizing: border-box;
      padding: 12px 12px 16px;
      border-radius: 0;
      border: 1px solid ${theme.v3.colors.gradients.blueGradient[0]};
    }
  `}
`;

export const CompactInput = styled.div<{ $fixedWidth?: boolean; $isRecording?: boolean; $panel?: boolean }>`
  ${({
    theme, $fixedWidth, $isRecording, $panel,
  }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 100%;
    box-sizing: border-box;
    background: ${theme.v3.colors.neutral[100]};
    padding: ${$isRecording ? '20px 12px' : '20px 4px 20px 12px'};
    border-radius: 1000px;

    ${$fixedWidth
    ? css`width: 249px;`
    : css`flex: 1; min-width: 0;`
}

    @media (max-width: ${theme.v3.breakpoints.md}) {
      flex-basis: 100%;
      width: 100%;
      height: 52px;
      box-sizing: border-box;
      padding: 14px 4px 14px 12px;
    }

    ${$panel && css`
      align-items: flex-start;
      height: 120px;
      padding: 12px 4px 20px 12px;
      border: none;
      border-radius: 22px;

      textarea {
        align-self: stretch;
        height: 100%;
      }
    `}
  `}
`;

export const Avatar = styled.div`
  ${({ theme }) => css`
    width: 24px;
    height: 24px;
    border-radius: 1000px;
    overflow: hidden;
    flex-shrink: 0;
    background: ${theme.v3.colors.brand.snappyBlue[95]};

    img {
      width: 100%;
      height: 100%;
      display: block;
    }
  `}
`;

export const InputField = styled.textarea`
  ${({ theme }) => css`
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
    padding: 0;
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.regular};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.neutral[20]};

    &::placeholder {
      color: ${theme.v3.colors.neutral[40]};
    }
  `}
`;

export const AudioButton = styled.button`
  ${({ theme }) => css`
    width: 36px;
    height: 36px;
    border-radius: 1000px;
    padding: 2px 5px;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.neutral[100]};
      outline-offset: 2px;
    }
  `}
`;

export const SuggestedOptions = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: center;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      flex: 1;
      justify-content: flex-start;
    }
  `}
`;

export const SuggestionChip = styled.button<{ $spanFull?: boolean }>`
  ${({ theme, $spanFull }) => css`
    height: 36px;
    min-width: 0;
    padding: 8px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.16);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    font: inherit;

    &:hover {
      background: rgba(255, 255, 255, 0.24);
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.neutral[100]};
      outline-offset: 2px;
    }

    ${$spanFull && css`
      @media (max-width: ${theme.v3.breakpoints.md}) {
        grid-column: 1 / -1;
      }
    `}
  `}
`;

export const SuggestionChipPlus = styled(SuggestionChip)`
  padding: 8px;
`;

export const SuggestionChipLabel = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: -0.154px;
    color: ${theme.v3.colors.neutral[100]};
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const VersionTag = styled.div`
  ${({ theme }) => css`
    width: 28px;
    padding: 2px 4px;
    border-radius: 38px;
    background: ${theme.v3.colors.neutral[100]};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `}
`;

export const VersionTagText = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.bold};
    font-size: 12px;
    line-height: normal;
    color: ${theme.v3.colors.brand.snappyBlue[50]};
  `}
`;

export const CenteredWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 16px;
`;

export const Wrapper = styled.div`
  ${({ theme }) => css`
    position: relative;
    display: inline-flex;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      display: flex;
      width: 100%;
    }
  `}
`;

export const Overlay = styled.div<{ $zIndex?: number }>`
  ${({ theme, $zIndex }) => css`
    position: fixed;
    inset: 0;
    visibility: visible;
    background: rgba(31, 32, 68, 0.4);
    backdrop-filter: blur(2px);
    z-index: ${$zIndex !== undefined ? $zIndex : theme.v3.layers.awaysOnTop};
  `}
`;

export const ExpandedPanel = styled.div<{ $top: number; $left: number; $width: number; $isMobile?: boolean }>`
  ${({
    theme, $top, $left, $width, $isMobile,
  }) => css`
    position: fixed;
    visibility: visible;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 20px;
    border: none;
    background: linear-gradient(
      to top,
      rgba(0, 122, 207, 0.48) 70%,
      rgba(210, 228, 255, 0.48) 100%
    );
    backdrop-filter: blur(16px);
    z-index: ${theme.v3.layers.important};
    top: ${$top}px;

    ${$isMobile
    ? css`
      left: 50%;
      width: ${$width}px;
      transform: translateX(-50%);
    `
    : css`
      left: ${$left}px;
      width: 752px;
      max-width: calc(100vw - ${$left + 16}px);
    `}
  `}
`;

export const ChipsGrid = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: ${theme.v3.breakpoints.md}) {
      grid-template-columns: repeat(2, 1fr);
    }
  `}
`;

export const SuggestionsLabel = styled.p`
  ${({ theme }) => css`
    margin: 0;
    padding: 4px 8px;
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.regular};
    font-size: 14px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.72);
  `}
`;

export const SuggestionsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
`;

export const SuggestionItem = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 8px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    font: inherit;

    &:hover:not(:disabled) {
      opacity: 0.8;
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.neutral[100]};
      outline-offset: 2px;
    }
  `}
`;

export const SuggestionItemText = styled.span`
  ${({ theme }) => css`
    flex: 1;
    min-width: 0;
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.regular};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.neutral[100]};
  `}
`;

export const SuggestionDivider = styled.hr`
  height: 1px;
  background-color: rgba(255, 255, 255, 0.16);
  border: none;
  margin: 0;
`;

export const RecordingContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  gap: 12px;
`;

export const RecordingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const RecordingAvatar = styled.div`
  ${({ theme }) => css`
    width: 24px;
    height: 24px;
    border-radius: 1000px;
    flex-shrink: 0;
    overflow: hidden;
    background: ${theme.v3.colors.brand.snappyBlue[95]};
    display: flex;
    align-items: flex-end;
    justify-content: center;

    img {
      width: 100%;
      display: block;
      object-fit: contain;
    }
  `}
`;

export const RecordingLabel = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[10]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

export const RecordingRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const RecordingTime = styled.span`
  ${({ theme }) => css`
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.brand.snappyBlue[10]};
    font-variant-numeric: tabular-nums;
  `}
`;

export const RecordingActionButton = styled.button<{ $variant: 'cancel' | 'send' }>`
  ${({ theme, $variant }) => css`
    width: 32px;
    height: 32px;
    border-radius: 12px;
    padding: 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    background: ${$variant === 'cancel'
    ? theme.v3.colors.contextual.problem[50]
    : theme.v3.colors.brand.snappyBlue[50]};

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.neutral[100]};
      outline-offset: 2px;
    }
  `}
`;

export const LearnMoreBar = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    width: 100%;
    padding: 0 8px 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: ${theme.v3.font.family.brand};
    font-weight: ${theme.v3.font.weights.medium};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.v3.colors.neutral[100]};

    &:focus-visible {
      outline: 2px solid ${theme.v3.colors.neutral[100]};
      outline-offset: 2px;
    }
  `}
`;

const edgesGlowPulse = keyframes`
  0%,
  100% {
    box-shadow:
      inset 0 0 8px 2px rgba(0, 122, 207, 0.85),
      inset 0 0 26px 5px rgba(0, 122, 207, 0.25);
  }

  50% {
    box-shadow:
      inset 0 0 28px 6px rgba(0, 122, 207, 0.95),
      inset 0 0 60px 16px rgba(0, 122, 207, 0.4);
  }
`;

export const EdgesGlow = styled.div<{ $zIndex?: number }>`
  ${({ theme, $zIndex }) => css`
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: ${$zIndex !== undefined ? $zIndex : theme.v3.layers.awaysOnTop};
    animation: ${edgesGlowPulse} 3s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      box-shadow: inset 0 0 16px 3px ${theme.v3.colors.brand.snappyBlue[50]};
    }
  `}
`;
